import { settingsDefaultValues } from './defaultValues';

import type { OrganizationFormValues, ShiftActivityTypeFormValues } from './types';
import type { Organization, OrganizationCreateDto } from '../../model/types/organization.dto';
import type { Settings } from '@/app/types/Settings';
import type { ShiftActivityType } from '@/app/types/ShiftActivityType';

const mapWorkingHoursToDto = (
	workingHours: OrganizationFormValues['settings']['shiftPlanner']['workingHours']
) => ({
	monday: {
		start_time_hours: workingHours.monday.start.hours,
		start_time_minutes: workingHours.monday.start.minutes,
		end_time_hours: workingHours.monday.end.hours,
		end_time_minutes: workingHours.monday.end.minutes,
	},
	tuesday: {
		start_time_hours: workingHours.tuesday.start.hours,
		start_time_minutes: workingHours.tuesday.start.minutes,
		end_time_hours: workingHours.tuesday.end.hours,
		end_time_minutes: workingHours.tuesday.end.minutes,
	},
	wednesday: {
		start_time_hours: workingHours.wednesday.start.hours,
		start_time_minutes: workingHours.wednesday.start.minutes,
		end_time_hours: workingHours.wednesday.end.hours,
		end_time_minutes: workingHours.wednesday.end.minutes,
	},
	thursday: {
		start_time_hours: workingHours.thursday.start.hours,
		start_time_minutes: workingHours.thursday.start.minutes,
		end_time_hours: workingHours.thursday.end.hours,
		end_time_minutes: workingHours.thursday.end.minutes,
	},
	friday: {
		start_time_hours: workingHours.friday.start.hours,
		start_time_minutes: workingHours.friday.start.minutes,
		end_time_hours: workingHours.friday.end.hours,
		end_time_minutes: workingHours.friday.end.minutes,
	},
	saturday: {
		start_time_hours: workingHours.saturday.start.hours,
		start_time_minutes: workingHours.saturday.start.minutes,
		end_time_hours: workingHours.saturday.end.hours,
		end_time_minutes: workingHours.saturday.end.minutes,
	},
	sunday: {
		start_time_hours: workingHours.sunday.start.hours,
		start_time_minutes: workingHours.sunday.start.minutes,
		end_time_hours: workingHours.sunday.end.hours,
		end_time_minutes: workingHours.sunday.end.minutes,
	},
});

export const mapSettingsToFormValues = (settings: Settings): OrganizationFormValues['settings'] => {
	return {
		...settingsDefaultValues,
		shiftPlanner: {
			...settingsDefaultValues.shiftPlanner,
			activities: [],
			workingHours: {
				monday: {
					start: {
						hours: settings.general.shift_planner.work_hours.monday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.monday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.monday.end_time_hours,
						minutes: settings.general.shift_planner.work_hours.monday.end_time_minutes,
					},
				},
				tuesday: {
					start: {
						hours: settings.general.shift_planner.work_hours.tuesday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.tuesday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.tuesday.end_time_hours,
						minutes: settings.general.shift_planner.work_hours.tuesday.end_time_minutes,
					},
				},
				wednesday: {
					start: {
						hours: settings.general.shift_planner.work_hours.wednesday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.wednesday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.wednesday.end_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.wednesday.end_time_minutes,
					},
				},
				thursday: {
					start: {
						hours: settings.general.shift_planner.work_hours.thursday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.thursday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.thursday.end_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.thursday.end_time_minutes,
					},
				},
				friday: {
					start: {
						hours: settings.general.shift_planner.work_hours.friday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.friday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.friday.end_time_hours,
						minutes: settings.general.shift_planner.work_hours.friday.end_time_minutes,
					},
				},
				saturday: {
					start: {
						hours: settings.general.shift_planner.work_hours.saturday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.saturday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.saturday.end_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.saturday.end_time_minutes,
					},
				},
				sunday: {
					start: {
						hours: settings.general.shift_planner.work_hours.sunday.start_time_hours,
						minutes:
							settings.general.shift_planner.work_hours.sunday.start_time_minutes,
					},
					end: {
						hours: settings.general.shift_planner.work_hours.sunday.end_time_hours,
						minutes: settings.general.shift_planner.work_hours.sunday.end_time_minutes,
					},
				},
			},
			zoomLevel: settings.general.shift_planner.default_zoom ?? 30,
			timeSelectionInterval: settings.general.shift_planner.default_intervals,
		},
		tasks: {
			...settingsDefaultValues.tasks,
			defaultTaskDuration: settings.general.tasks.frontend.end_duration_minutes,
			defaultRemindersActive: settings.general.tasks.defaults.config.reminders_enabled,
			reminderBeforeStart: {
				active: settings.general.tasks.defaults.config.start_reminder_enabled,
				value: settings.general.tasks.defaults.config.start_reminder_time,
			},
			reminderBeforeEnd: {
				active: settings.general.tasks.defaults.config.end_reminder_enabled,
				value: settings.general.tasks.defaults.config.end_reminder_time,
			},
			reminderOverdue: {
				active: settings.general.tasks.defaults.config.complete_reminder_enabled,
				value: settings.general.tasks.defaults.config.complete_reminder_time,
			},
		},
		actions: {
			...settingsDefaultValues.actions,
			autoForward: [
				settings.general.actions.frontend.autoforward_enum.medium,
				settings.general.actions.frontend.autoforward_enum.normal,
				settings.general.actions.frontend.autoforward_enum.urgent,
			],
			sendFeedback: settings.general.actions.defaults.config.send_feedback,
			acceptanceFeedback: {
				active: settings.general.actions.defaults.config.acceptance_feedback,
				value: settings.general.actions.defaults.config.acceptance_feedback_content,
			},
			rejectionFeedback: {
				active: settings.general.actions.defaults.config.rejection_feedback,

				value: settings.general.actions.defaults.config.rejection_feedback_content,
			},
			acceptedByMessage: settings.general.actions.defaults.config.send_feedback,
			acceptedByMessageTitle:
				settings.general.actions.defaults.config
					.accepted_by_other_custom_alert_screen_title,
			acceptedByMessageBody:
				settings.general.actions.defaults.config.accepted_by_other_custom_alert_screen_body,
			completionConfirmation:
				settings.general.actions.defaults.config.demand_action_completion,
			useSplashScreen:
				settings.general.actions.defaults.config.is_action_alert_screen_enabled,
			incomingMessageDuration:
				settings.general.actions.defaults.config.action_alert_screen_duration_in_ms,
		},
		automation: {
			...settingsDefaultValues.automation,
			blockMultiplePresses: settings.general.automation.defaults.throttle_enabled,
			minTimeBetweenPushes: settings.general.automation.defaults.throttle_threshold,
		},
		tablet: {
			...settingsDefaultValues.tablet,
			status: settings.tablet.show_screen_status,
			workingHours: {
				monday: {
					start: {
						hours: settings.tablet.show_screen_schedule.monday.start_time_hours,

						minutes: settings.tablet.show_screen_schedule.monday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.monday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.monday.end_time_minutes,
					},
				},
				tuesday: {
					start: {
						hours: settings.tablet.show_screen_schedule.tuesday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.tuesday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.tuesday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.tuesday.end_time_minutes,
					},
				},
				wednesday: {
					start: {
						hours: settings.tablet.show_screen_schedule.wednesday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.wednesday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.wednesday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.wednesday.end_time_minutes,
					},
				},
				thursday: {
					start: {
						hours: settings.tablet.show_screen_schedule.thursday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.thursday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.thursday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.thursday.end_time_minutes,
					},
				},
				friday: {
					start: {
						hours: settings.tablet.show_screen_schedule.friday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.friday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.friday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.friday.end_time_minutes,
					},
				},
				saturday: {
					start: {
						hours: settings.tablet.show_screen_schedule.saturday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.saturday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.saturday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.saturday.end_time_minutes,
					},
				},
				sunday: {
					start: {
						hours: settings.tablet.show_screen_schedule.sunday.start_time_hours,
						minutes: settings.tablet.show_screen_schedule.sunday.start_time_minutes,
					},
					end: {
						hours: settings.tablet.show_screen_schedule.sunday.end_time_hours,
						minutes: settings.tablet.show_screen_schedule.sunday.end_time_minutes,
					},
				},
			},
			templates: [],
			noResponseTimer: settings.tablet.no_response_screen_timer,
		},
		watch: {
			...settingsDefaultValues.watch,
			welcomeMessage: settings.watch.login_message.title?.text,
			goodbyeMessage: settings.watch.logout_message.title?.text,
			callForHelp: settings.watch.menu_trigger_group_id,
		},
	};
};

export const mapFormValuesToSettingsDto = (
	settings: OrganizationFormValues['settings'],
	defaultDto?: Settings
): Settings => {
	return {
		...defaultDto,
		general: {
			...defaultDto?.general,
			shift_planner: {
				...defaultDto?.general.shift_planner,
				work_hours: mapWorkingHoursToDto(settings.shiftPlanner.workingHours),
				default_zoom: settings.shiftPlanner.zoomLevel,
				default_intervals: settings.shiftPlanner.timeSelectionInterval,
			},
			tasks: {
				...defaultDto?.general.tasks,
				frontend: {
					...defaultDto?.general.tasks.frontend,
					end_duration_minutes: settings.tasks.defaultTaskDuration,
				},
				defaults: {
					...defaultDto?.general.tasks.defaults,
					config: {
						...defaultDto?.general.tasks.defaults.config,
						reminders_enabled: settings.tasks.defaultRemindersActive,
						start_reminder_enabled: settings.tasks.reminderBeforeStart.active,
						start_reminder_time: settings.tasks.reminderBeforeStart.value,
						end_reminder_enabled: settings.tasks.reminderBeforeEnd.active,
						end_reminder_time: settings.tasks.reminderBeforeEnd.value,
						complete_reminder_enabled: settings.tasks.reminderOverdue.active,
						complete_reminder_time: settings.tasks.reminderOverdue.value,
					},
				},
			},
			actions: {
				...defaultDto?.general.actions,
				frontend: {
					...defaultDto?.general.actions.frontend,
					autoforward_enum: {
						...defaultDto?.general.actions.frontend.autoforward_enum,
						medium: settings.actions.autoForward[0],
						normal: settings.actions.autoForward[1],
						urgent: settings.actions.autoForward[2],
					},
				},
				defaults: {
					...defaultDto?.general.actions.defaults,
					config: {
						...defaultDto?.general.actions.defaults.config,
						send_feedback: settings.actions.sendFeedback,
						acceptance_feedback: settings.actions.acceptanceFeedback.active,
						acceptance_feedback_content: settings.actions.acceptanceFeedback.value,
						rejection_feedback: settings.actions.rejectionFeedback.active,
						rejection_feedback_content: settings.actions.rejectionFeedback.value,
						accepted_by_other_custom_alert_screen_title:
							settings.actions.acceptedByMessageTitle,
						accepted_by_other_custom_alert_screen_body:
							settings.actions.acceptedByMessageBody,
						demand_action_completion: settings.actions.completionConfirmation,
						is_action_alert_screen_enabled: settings.actions.useSplashScreen,
						action_alert_screen_duration_in_ms:
							settings.actions.incomingMessageDuration,
					},
				},
			},
			automation: {
				...defaultDto?.general.automation,
				defaults: {
					...defaultDto?.general.automation.defaults,
					throttle_enabled: settings.automation.blockMultiplePresses,
					throttle_threshold: settings.automation.minTimeBetweenPushes,
				},
			},
		},
		tablet: {
			...defaultDto?.tablet,
			show_screen_status: settings.tablet.status,
			show_screen_schedule: mapWorkingHoursToDto(settings.tablet.workingHours),
			no_response_screen_timer: settings.tablet.noResponseTimer,
		},
		watch: {
			...defaultDto?.watch,

			login_message: {
				...defaultDto?.watch?.login_message,
				title: {
					...defaultDto?.watch?.login_message?.title,
					text: settings.watch.welcomeMessage,
				},
			},
			logout_message: {
				...defaultDto?.watch?.logout_message,
				title: {
					...defaultDto?.watch?.logout_message?.title,
					text: settings.watch.goodbyeMessage,
				},
			},
			...defaultDto?.watch,
			menu_trigger_group_id: settings.watch.callForHelp,
		},
	} as Settings;
};

export const mapFormValuesToDto = (values: OrganizationFormValues): OrganizationCreateDto => {
	const { parentOrganization, organizationType, name, timeZone, city, country, address } =
		values.general;

	const workingHours: Record<
		string,
		{
			start: {
				hours: number;
				minutes: number;
			};
			end: {
				hours: number;
				minutes: number;
			};
		}
	> = values.settings?.shiftPlanner?.workingHours ?? {};

	const createWorkingDay = (day: string) => {
		const startHours = workingHours[day].start.hours.toString().padStart(2, '0');
		const startMinutes = workingHours[day].start.minutes.toString().padStart(2, '0');
		const endHours = workingHours[day].end.hours.toString().padStart(2, '0');
		const endMinutes = workingHours[day].end.minutes.toString().padStart(2, '0');

		return `${startHours}:${startMinutes}-${endHours}:${endMinutes}`;
	};

	return {
		organization: {
			parent_id: parentOrganization,
			type: organizationType,
			name,
			time_zone: timeZone ?? '',
			street_address: {
				street: address,
				city,
				country,
				longitude: 0,
				latitude: 0,
			},
		},
		users: [
			...(values.users.users ?? []).map((user) => ({
				email: user.email,
				first_name: user.firstName,
				last_name: user.lastName,
				access_level: 'employee',
				password: user.password,
				pin: user.pin,
			})),
		],
		shift_activity_types:
			values.settings?.shiftPlanner?.activities?.map((activity) => ({
				name: activity.name,
				color: activity.color,
			})) ?? [],
		settings: {
			portal: values.general?.organizationLogo
				? {
						logo: values.general?.organizationLogo ?? undefined,
					}
				: undefined,
			actions: {
				auto_forward_after_seconds: values.settings?.actions?.autoForward ?? [],
				is_feedback_enabled: values.settings?.actions?.sendFeedback ?? false,
				feedback_when_accepted: {
					is_enabled: values.settings?.actions?.acceptanceFeedback?.active ?? false,
					message: values.settings?.actions?.acceptanceFeedback?.value ?? '',
				},
				feedback_when_declined: {
					is_enabled: values.settings?.actions?.rejectionFeedback?.active ?? false,
					message: values.settings?.actions?.rejectionFeedback?.value ?? '',
				},
				is_accepted_by_other_enabled: values.settings?.actions?.acceptedByMessage ?? false,
				accepted_by_other_title: values.settings?.actions?.acceptedByMessageTitle ?? '',
				accepted_by_other_body: values.settings?.actions?.acceptedByMessageBody ?? '',
				is_completed_by_user_required:
					values.settings?.actions?.completionConfirmation ?? false,
				is_alert_screen_enabled: values.settings?.actions?.useSplashScreen ?? false,
				alert_screen_duration_in_milliseconds:
					values.settings?.actions?.incomingMessageDuration ?? 0,
			},
			tasks: {
				default_task_duration_in_minutes: values.settings?.tasks?.defaultTaskDuration ?? 0,
				is_reminders_enabled: values.settings?.tasks?.defaultRemindersActive ?? false,
				start_reminder: {
					is_enabled: values.settings?.tasks?.reminderBeforeStart?.active ?? false,
					value: values.settings?.tasks?.reminderBeforeStart?.value ?? 0,
				},
				end_reminder: {
					is_enabled: values.settings?.tasks?.reminderBeforeEnd?.active ?? false,
					value: values.settings?.tasks?.reminderBeforeEnd?.value ?? 0,
				},
				overdue_reminder: {
					is_enabled: values.settings?.tasks?.reminderOverdue?.active ?? false,
					value: values.settings?.tasks?.reminderOverdue?.value ?? 0,
				},
				overdue_reminder_interval: values.settings?.tasks?.reminderOverdueInterval ?? 0,
				overdue_reminders_max: values.settings?.tasks?.overdueRemindersNumber ?? 0,
			},
			shifts: {
				working_hours: {
					monday: createWorkingDay('monday'),
					tuesday: createWorkingDay('tuesday'),
					wednesday: createWorkingDay('wednesday'),
					thursday: createWorkingDay('thursday'),
					friday: createWorkingDay('friday'),
					saturday: createWorkingDay('saturday'),
					sunday: createWorkingDay('sunday'),
				},
				time_selection_interval: values.settings?.shiftPlanner?.timeSelectionInterval ?? 0,
				zoom_level: values.settings?.shiftPlanner?.zoomLevel ?? 0,
			},
			tablets: {
				no_response_period_in_seconds: values.settings?.tablet?.noResponseTimer ?? 0,
			},
			triggers: {
				should_throttle: values.settings?.automation?.blockMultiplePresses ?? false,
				throttle_period_in_seconds: values.settings?.automation?.minTimeBetweenPushes ?? 0,
			},
			watches: {
				login_message: values.settings?.watch?.welcomeMessage ?? '',
				logout_message: values.settings?.watch?.goodbyeMessage ?? '',
				logo: values.settings?.watch?.logo ?? undefined,
				return_to_watch_face_after_milliseconds:
					values.settings?.watch?.watchFaceForceReturn ?? 0,
			},
		},
	};
};

export const mapGeneralInfoToDto = (values: OrganizationFormValues['general']): Organization => {
	return {
		id: values.id,
		parent_id: values.parentOrganization,
		type: values.organizationType,
		name: values.name,
		time_zone: values.timeZone ?? '',
		street_address: {
			street: values.address,
			city: values.city,
			country: values.country,
			longitude: 0,
			latitude: 0,
		},
	};
};

export const mapShiftActivityToFormValues = (
	settingsFormValues: OrganizationFormValues['settings'],
	activityFormValues: ShiftActivityTypeFormValues,
	activity: ShiftActivityType
): OrganizationFormValues['settings'] => {
	return {
		...settingsFormValues,
		shiftPlanner: {
			...settingsFormValues.shiftPlanner,
			activities: [
				...(settingsFormValues.shiftPlanner.activities ?? []).map((item) => {
					if (item.clientId === activityFormValues.clientId) {
						return {
							...item,
							clientId: activity.id,
							serverId: activity.id,
							name: activity.name,
							color: activity.color,
						};
					}

					return {
						...item,
					};
				}),
			],
		},
	};
};
